import { useState } from 'react';
import cx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { MultiSelect } from '@repo/design-system-kit/components/multi-select/multi-select';
import { TextField } from '@repo/design-system-kit/components/text-field/text-field';
import type { Key } from 'react-aria';
import { getBusinessRelationshipsOptions } from './utils';
import styles from './styles.module.css';

interface BusinessRelationshipsDropdownProps {
  className?: string;
  initialSelectedKeys?: Set<string>;
  otherValue: string;
  onOtherValueChange?: (value: string) => void;
  onSelectionChange?: (keys: string[]) => void;
  renderOtherField?: boolean;
  selectedKeys?: string[];
  shouldShowValidationError?: boolean;
  errorMessage?: string;
  isAssociation?: boolean;
  isFoundation?: boolean;
  useV2Options?: boolean;
}

export function BusinessRelationshipsDropdown({
  className,
  initialSelectedKeys = new Set(),
  otherValue,
  onOtherValueChange,
  onSelectionChange,
  renderOtherField = true,
  selectedKeys: controlledSelectedKeys,
  shouldShowValidationError = false,
  errorMessage,
  isAssociation = false,
  isFoundation = false,
  useV2Options = false,
}: BusinessRelationshipsDropdownProps): React.ReactNode {
  const { formatMessage } = useIntl();
  const [uncontrolledSelectedKeys, setUncontrolledSelectedKeys] =
    useState<Set<string>>(initialSelectedKeys);
  const selectedKeys = controlledSelectedKeys
    ? new Set(controlledSelectedKeys)
    : uncontrolledSelectedKeys;

  let label = formatMessage({
    id: 'customer-registration-kit.business-activity.source-of-funds.label',
  });
  if (useV2Options) {
    label = formatMessage({
      id: 'customer-registration-kit.organization-activity.source-of-funds.label',
    });
  } else if (isFoundation) {
    label = formatMessage({
      id: 'customer-registration-kit.business-activity.source-of-funds-foundation.label',
    });
  } else if (isAssociation) {
    label = formatMessage({
      id: 'customer-registration-kit.business-activity.source-of-funds-association.label',
    });
  }

  const options = getBusinessRelationshipsOptions({
    useV2Options,
    isAssociation,
    isFoundation,
  }).map(({ value, i18nKey }) => ({
    key: value,
    value: formatMessage({
      // oxlint-disable-next-line @repo/no-dynamic-intl-id
      id: useV2Options
        ? `customer-registration-kit.organization-activity.source-of-funds.${i18nKey}.option`
        : `customer-registration-kit.business-activity.source-of-funds.options.${i18nKey}`,
    }),
  }));

  const shouldShowOtherField = renderOtherField && selectedKeys.has('other');
  const hasNoSelection = selectedKeys.size === 0;
  const shouldShowError = shouldShowValidationError && hasNoSelection;

  const handleSelectionChange = (keys: Set<Key>): void => {
    const nextSelectedKeys = Array.from(keys).map(key => String(key));

    if (!controlledSelectedKeys) {
      setUncontrolledSelectedKeys(new Set(nextSelectedKeys));
    }

    onSelectionChange?.(nextSelectedKeys);
  };

  return (
    <div className={className}>
      <input
        name="business_relationships"
        type="hidden"
        value={Array.from(selectedKeys).join(',')}
      />
      <MultiSelect
        errorMessage={
          shouldShowError
            ? (errorMessage ?? formatMessage({ id: 'generic.required-field.error' }))
            : undefined
        }
        isInvalid={shouldShowError}
        isOptional={false}
        items={options}
        label={label}
        onSelectionChange={handleSelectionChange}
        placeholder={options[0]?.value || ''}
        selectedKeys={selectedKeys}
        withSearch={{
          placeholder: formatMessage({
            id: 'customer-registration-kit.list-select.search.placeholder',
          }),
          emptyMessage: formatMessage({
            id: 'generic.no-results.error',
          }),
        }}
      />
      {!shouldShowError && (
        <p className={cx('caption', styles.inputCaption)}>
          <FormattedMessage
            id={
              useV2Options
                ? 'customer-registration-kit.organization-activity.source-of-funds.helper'
                : 'customer-registration-kit.business-activity.source-of-funds.helper-text'
            }
          />
        </p>
      )}
      {shouldShowOtherField ? (
        <TextField
          aria-label={formatMessage({
            id: 'customer-registration-kit.business-activity.source-of-funds.options.other-placeholder',
          })}
          defaultValue={otherValue}
          onChange={onOtherValueChange}
          isRequired
          label=""
          name="business_relationships_other"
          placeholder={formatMessage({
            id: 'customer-registration-kit.business-activity.source-of-funds.options.other-placeholder',
          })}
        />
      ) : null}
    </div>
  );
}
